import React, { useEffect, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, LayersControl, LayerGroup, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from "react-leaflet-cluster";
import { Box, Button } from '@mui/material';
import L from 'leaflet';
import { ArrowUpRight, Hospital } from 'lucide-react';
import { renderToString } from 'react-dom/server';
import 'leaflet/dist/leaflet.css';
import './Hospitals.css';
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";
import CommonTable from "../../../common/CommonTable";

// Configure Leaflet to remove default icon dependency
delete L.Icon.Default.prototype._getIconUrl;

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Function to create a hospital icon marker
const createHospitalMarker = (color = '#669870', size = 15) => {
  const hospitalIcon = renderToString(
    <Hospital size={20} color="white" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
  );
  return L.divIcon({
    html: `
      <div class="hospital-marker" style="background-color: ${color};">
        ${hospitalIcon}
      </div>
    `,
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
};

// Color map for facility types (used for marker colors)
const colorMap = {
  'Health Post': { marker: createHospitalMarker('#FF0000'), color: '#FF0000' }, // Red
  'Community Health Center': { marker: createHospitalMarker('#0000FF'), color: '#0000FF' }, // Blue
  'Community Health Center (Internment)': { marker: createHospitalMarker('#00FF00'), color: '#00FF00' }, // Green
  'Clinic': { marker: createHospitalMarker('#FFA500'), color: '#FFA500' }, // Orange
  'Pharmacy': { marker: createHospitalMarker('#800080'), color: '#800080' }, // Purple
  'Referral Hospital': { marker: createHospitalMarker('#FFC0CB'), color: '#FFC0CB' }, // Pink
  'Clinic and Pharmacy': { marker: createHospitalMarker('#008080'), color: '#008080' }, // Teal
  'Regional Hospital': { marker: createHospitalMarker('#FFD700'), color: '#FFD700' }, // Gold
  'National Hospital': { marker: createHospitalMarker('#FF4500'), color: '#FF4500' }, // OrangeRed
  'Disability Rehabilitation Center': { marker: createHospitalMarker('#6B8E23'), color: '#6B8E23' }, // OliveDrab
  'Multi Drug Rehabilitation Center': { marker: createHospitalMarker('#20B2AA'), color: '#20B2AA' }, // LightSeaGreen
  'Mental Rehabilitation Center': { marker: createHospitalMarker('#9932CC'), color: '#9932CC' }, // DarkOrchid
};

// Legend content — Ocean State collapsible legend format
const hospitalLegendIcon = (color) => (
  <div
    style={{
      width: "16px",
      height: "16px",
      backgroundColor: color,
      borderRadius: "50%",
      border: "1px solid #ddd",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Hospital size={10} color="white" />
  </div>
);

const LegendSection = ({ categories }) => {
  const items = [
    ...categories.map((category) => ({
      label: category.label,
      icon: hospitalLegendIcon(category.color),
    })),
    { label: "Cluster of Facilities", color: "#0f6cbd" },
  ];
  return <MapLegendControl sections={[{ title: "Facility Types", items }]} />;
};

// Component to handle map events
const MapEvents = ({ setSelectedCode, codeToFacilityMap }) => {
  useMapEvents({
    baselayerchange: (event) => {
      const layerName = event.name;
      // Find the code corresponding to the selected facility_type, or use "Select All"
      const selectedCode = Object.keys(codeToFacilityMap).find(
        (code) => codeToFacilityMap[code] === layerName
      ) || layerName;
      setSelectedCode(selectedCode);
    },
  });
  return null;
};

function Hospitals() {
  const [loading, setLoading] = useState(true);
  const [hospitals, setHospitals] = useState([]);
  const [selectedCode, setSelectedCode] = useState('Select All');

  const fetchHospitals = async () => {
    try {
      const response = await axios.get(`${backendUrl}roadaccident/get_hospital_details/`);
      console.log('API Response:', response.data);
      setHospitals(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching hospital data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHospitals();
  }, []);

  // Create a mapping of code to facility_type
  const codeToFacilityMap = hospitals.reduce((acc, hospital) => {
    const code = hospital.code || 'Unknown';
    const facilityType = hospital.facility_type || 'Unknown';
    if (!acc[code]) {
      acc[code] = facilityType;
    }
    return acc;
  }, {});

  // Filter hospitals for both map and table based on selected code
  const filteredHospitals = selectedCode === 'Select All'
    ? hospitals
    : hospitals.filter((hospital) => (hospital.code || 'Unknown') === selectedCode);

  // Get hospitals for the "Select All" layer
  const allHospitalsForMap = filteredHospitals;

  // Get hospitals for each specific code layer
  const getHospitalsForCode = (code) => {
    return filteredHospitals.filter((hospital) => (hospital.code || 'Unknown') === code);
  };

  const columns = [
    { key: 'SlNo', label: 'S.No' },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'facility_type', label: 'Facility Type', sortable: true },
    { key: 'code', label: 'Code', sortable: true },
    { key: 'suco', label: 'Suco', sortable: true },
    { key: 'municipality', label: 'Municipality', sortable: true },
    { key: 'adm2', label: 'Administrative Post', sortable: true },
    { key: 'latitude', label: 'Latitude', align: 'right' },
    { key: 'longitude', label: 'Longitude', align: 'right' },
    { key: 'elevation', label: 'Elevation (m)', align: 'right' },
    { key: 'property_type', label: 'Property Type', sortable: true },
    { key: 'operating_days', label: 'Operating Days' },
    { key: 'operating_hours', label: 'Operating Hours' },
    { key: 'total_beds', label: 'Total Beds', align: 'right' },
    { key: 'maternity_beds', label: 'Maternity Beds', align: 'right' },
    {
      key: 'has_ambulance',
      label: 'Has Ambulance',
      render: (row) => (row.has_ambulance ? 'Yes' : 'No'),
    },
    {
      key: 'has_emergency',
      label: 'Has Emergency',
      render: (row) => (row.has_emergency ? 'Yes' : 'No'),
    },
    { key: 'location', label: 'Location' },
  ];

  const openGoogleMapsDirections = (lat, lng) => {
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(googleMapsUrl, '_blank');
  };

  return (
    <div className="">
      {!loading && hospitals.length > 0 && (
        <Box mb={4}>
          <MapContainer
            center={hospitals[0]?.latitude && hospitals[0]?.longitude ? [hospitals[0].latitude, hospitals[0].longitude] : [-8.6, 125.6]}
            zoom={8.5}
            style={{ height: "600px", width: "100%" }}
          >
            <MapHomeControl />
            <MapInfoControl text="Locations of hospitals and health facilities for road accident emergency response." />
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapEvents setSelectedCode={setSelectedCode} codeToFacilityMap={codeToFacilityMap} />
            <LayersControl position="topright">
              <LayersControl.BaseLayer name="Select All" checked={selectedCode === 'Select All'}>
                <LayerGroup>
                  <MarkerClusterGroup
                    chunkedLoading
                    iconCreateFunction={(cluster) => {
                      const count = cluster.getChildCount();
                      return L.divIcon({
                        html: `<div class="custom-cluster" style="background-color: #0f6cbd">${count}</div>`,
                        className: "custom-cluster-wrapper",
                        iconSize: L.point(40, 40, true),
                      });
                    }}
                  >
                    {allHospitalsForMap.map((hospital, idx) => (
                      hospital.latitude && hospital.longitude ? (
                        <Marker
                          key={`all-${idx}`}
                          position={[hospital.latitude, hospital.longitude]}
                          icon={colorMap[hospital.facility_type]?.marker || createHospitalMarker('#669870')}
                        >
                          <Popup>
                            <strong>{hospital.name || 'N/A'}</strong><br />
                            <strong>Code:</strong> {hospital.code || 'N/A'}<br />
                            <strong>Type:</strong> {hospital.facility_type || 'N/A'}<br />
                            <strong>Municipality:</strong> {hospital.municipality || 'N/A'}<br />
                            <strong>Administrative Post:</strong> {hospital.adm2 || 'N/A'}<br />
                            <strong>Suco:</strong> {hospital.suco || 'N/A'}<br />
                            <strong>Location:</strong> {hospital.location || 'N/A'}<br />
                            <strong>Operating Days:</strong> {hospital.operating_days || 'N/A'}<br />
                            <strong>Total Beds:</strong> {hospital.total_beds || 'N/A'}<br />
                            <strong>Maternity Beds:</strong> {hospital.maternity_beds || 'N/A'}<br />
                            <strong>Emergency:</strong> {hospital.has_emergency ? 'Yes' : 'No'}<br />
                            <strong>Ambulance:</strong> {hospital.has_ambulance ? 'Yes' : 'No'}<br />
                            <Button
                              onClick={() => openGoogleMapsDirections(hospital.latitude, hospital.longitude)}
                              style={{ textTransform: 'none', backgroundColor: 'rgb(234, 243, 240)', color: 'rgb(0, 0, 0)', marginTop: '0px', marginRight: '5px', fontSize: '10px', padding: '2px 7px', borderRadius: '9px' }}
                            >
                              Get Directions
                              <ArrowUpRight size={12} color="rgb(0, 0, 0)" />
                            </Button>
                          </Popup>
                        </Marker>
                      ) : null
                    ))}
                  </MarkerClusterGroup>
                </LayerGroup>
              </LayersControl.BaseLayer>
              {Object.entries(codeToFacilityMap).map(([code, facilityType]) => (
                <LayersControl.BaseLayer
                  key={code}
                  name={facilityType}
                  checked={selectedCode === code}
                >
                  <LayerGroup>
                    <MarkerClusterGroup
                      chunkedLoading
                      iconCreateFunction={(cluster) => {
                        const count = cluster.getChildCount();
                        const firstHospital = cluster.getAllChildMarkers()[0]?.options?.hospital;
                        const facilityType = firstHospital?.facility_type || 'Unknown';
                        return L.divIcon({
                          html: `<div class="custom-cluster" style="background-color: ${colorMap[facilityType]?.color || '#669870'}">${count}</div>`,
                          className: "custom-cluster-wrapper",
                          iconSize: L.point(40, 40, true),
                        });
                      }}
                    >
                      {getHospitalsForCode(code).map((hospital, idx) => (
                        hospital.latitude && hospital.longitude ? (
                          <Marker
                            key={`${code}-${idx}`}
                            position={[hospital.latitude, hospital.longitude]}
                            icon={colorMap[hospital.facility_type]?.marker || createHospitalMarker('#669870')}
                            hospital={hospital}
                          >
                            <Popup>
                              <strong>{hospital.name || 'N/A'}</strong><br />
                              <strong>Code:</strong> {hospital.code || 'N/A'}<br />
                              <strong>Type:</strong> {hospital.facility_type || 'N/A'}<br />
                              <strong>Municipality:</strong> {hospital.municipality || 'N/A'}<br />
                              <strong>Administrative Post:</strong> {hospital.adm2 || 'N/A'}<br />
                              <strong>Suco:</strong> {hospital.suco || 'N/A'}<br />
                              <strong>Location:</strong> {hospital.location || 'N/A'}<br />
                              <strong>Operating Days:</strong> {hospital.operating_days || 'N/A'}<br />
                              <strong>Total Beds:</strong> {hospital.total_beds || 'N/A'}<br />
                              <strong>Maternity Beds:</strong> {hospital.maternity_beds || 'N/A'}<br />
                              <strong>Emergency:</strong> {hospital.has_emergency ? 'Yes' : 'No'}<br />
                              <strong>Ambulance:</strong> {hospital.has_ambulance ? 'Yes' : 'No'}<br />
                              <Button
                                onClick={() => openGoogleMapsDirections(hospital.latitude, hospital.longitude)}
                                style={{ textTransform: 'none', backgroundColor: 'rgb(234, 243, 240)', color: 'rgb(0, 0, 0)', marginTop: '0px', marginRight: '5px', fontSize: '10px', padding: '2px 7px', borderRadius: '9px' }}
                              >
                                Get Directions
                                <ArrowUpRight size={12} color="rgb(0, 0, 0)" />
                              </Button>
                            </Popup>
                          </Marker>
                        ) : null
                      ))}
                    </MarkerClusterGroup>
                  </LayerGroup>
                </LayersControl.BaseLayer>
              ))}
            </LayersControl>
            {/* Legend */}
            <LegendSection
              categories={Object.keys(colorMap).map((key) => ({
                label: key,
                color: colorMap[key].color,
              }))}
            />
          </MapContainer>
        </Box>
      )}
      {loading ? (
        <div className="text-center">Loading...</div>
      ) : hospitals.length === 0 ? (
        <div className="text-center">No hospital data available.</div>
      ) : (
        <CommonTable
          tableData={filteredHospitals}
          columns={columns}
          searchableKeys={['name', 'facility_type', 'code', 'suco', 'municipality', 'adm2', 'location']}
          tableName="Hospitals_Directory"
          emptyMessage="No hospital data available."
        />
      )}
    </div>
  );
}

export default Hospitals;